/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/core-client/js/core-client/ui/core/View',
	'text!./templates/ListView.template',
	'text!./templates/ListItems.template',
	'jquery',
	'underscore',
	'doT',
	'../nls/StringResources',
	'../lib/@waca/core-client/js/core-client/errors/BaseError',
	'react-dom',
	'./CenterLoadingSpinner'
], function(BaseView, ViewTemplate, ItemsTemplate, $, _, dot, stringResources, BaseError, ReactDOM, CenterLoadingSpinner) {

	/**
	 * Base list view provides a basic list control.
	 * If you extend this class you should implement the following methods
	 * @see getCustomRenderProperties - if you require specific rendering properties.
	 * You may optionally hook into the following callbacks.
	 * @ses renderComplete
	 */
	var View = BaseView.extend({

		templateString: ViewTemplate,
		itemTemplate: '<div class="caption">{{? item.label }}{{!item.label}}{{??}}{{!item.name}}{{?}}</div>',
		listTemplate: ItemsTemplate,
		itemClass: 'listitem',
		controlClass: 'list',
		treeItemClass: 'treeitem',
		treeControlClass: 'tree',
		listItemClass: 'listitem',
		listControlClass: 'list',
		isTree: false,

		init: function(options) {
			if (!this._baseEvents && this.events) {
				this._baseEvents = this.events;
			}
			if (options && options.isTree) {
				this.isTree = true;
			}
			this.setIsTree(this.isTree);

			View.inherited('init', this, arguments);

			this.dotTemplate = dot.template(this.templateString || '', undefined, {
				listTemplate: this.listTemplate,
				itemTemplate: this.itemTemplate
			});
			this.dotListTemplate = dot.template(this.listTemplate, undefined, {
				itemTemplate: this.itemTemplate
			});

			this.$el.addClass('baseListView');
		},

		_setupEvents: function() {
			if (this._baseEvents) {
				this.events = _.clone(this._baseEvents);
			} else {
				this.events = {};
			}

			this.events['click ' + this.itemClassSelector] = 'onSelectItem';
			this.events['keydown ' + this.itemClassSelector] = 'onKeyDown';
			this.events['focus ' + this.itemClassSelector] = 'onItemFocus';
			this.events['click .treeitem'] = 'onToggleItem';
		},

		setIsTree: function(isTree) {
			this.isTree = isTree ? true : false;
			if (this.isTree) {
				this.itemClass = this.treeItemClass;
				this.controlClass = this.treeControlClass;
			} else {
				this.itemClass = this.listItemClass;
				this.controlClass = this.listControlClass;
			}
			this.itemClassSelector = '.' + this.itemClass;
			this.controlClassSelector = '.' + this.controlClass;
			this._setupEvents();
		},

		rightArrowHandler: function($target) {
			return this.downArrowHandler($target);
		},

		leftArrowHandler: function($target) {
			return this.upArrowHandler($target);
		},

		downArrowHandler: function($target) {
			var $items = this.$(this.itemClassSelector + ':visible');
			return $($items[$items.index($target) + 1]);
		},

		upArrowHandler: function($target) {
			var $items = this.$(this.itemClassSelector + ':visible');
			return $($items[$items.index($target) - 1]);
		},

		onKeyDown: function(e) {
			var $target = $(this.getTarget(e.target, this.itemClass)),
				$nextItem = null;
			var keyCode = e.keyCode || e.charCode;
			var expandArrow, collapseArrow, nextHorizontalArrow, prevHorizontalArrow;
			if (this.isTree) { // Set up left/right arrow to act differently in tree/list
				expandArrow = 39; // right arrow
				collapseArrow = 37; // left arrow
				nextHorizontalArrow = -5;
				prevHorizontalArrow = -5;
			} else {
				expandArrow = -5;
				collapseArrow = -5;
				nextHorizontalArrow = 39; // right arrow
				prevHorizontalArrow = 37; // left arrow
			}
			switch (keyCode) {
				case 13:
				case 32:
					e.preventDefault();
					this.onSelectItem(e);
					break;
				case nextHorizontalArrow:
					e.preventDefault();
					$nextItem = this.rightArrowHandler($target);
					break;
				case 40: // down arrow
					e.preventDefault();
					$nextItem = this.downArrowHandler($target);
					break;
				case prevHorizontalArrow:
					e.preventDefault();
					$nextItem = this.leftArrowHandler($target);
					break;
				case 38: // up arrow
					e.preventDefault();
					$nextItem = this.upArrowHandler($target);
					break;
				case 35: // end
					e.preventDefault();
					$nextItem = this.$(this.itemClassSelector + ':visible').last();
					break;
				case 36: // home
					e.preventDefault();
					$nextItem = this.$(this.itemClassSelector + ':visible').first();
					break;
				case 107: // +
				case expandArrow:
					e.preventDefault();
					this.expandItem($target);
					break;
				case 109: // -
				case collapseArrow:
					e.preventDefault();
					this.collapseItem($target);
					break;
				case 46: // delete
					e.preventDefault();
					this.onDeleteItem(e);
					break;
				default:
					break;
			}

			if ($nextItem && $nextItem.length) {
				this.setFocusOnItem($nextItem);
			}
		},

		_getCurrentlyFocusedItems: function() {
			return this.$(this.itemClassSelector + '[tabindex="0"]');
		},

		_triggerFocusEvent: function($target) {
			this.trigger('focus:item', {
				id: $target.attr('data-id')
			});
		},


		onItemFocus: function(event, bUpdateOnly) {
			var $target = $(this.getTarget(event.target, this.itemClass));

			var focusedItems = this._getCurrentlyFocusedItems($target);
			var focusedNode = focusedItems.length && focusedItems.first()[0];
			if (focusedNode === $target[0]) {
				// item already focused.
				return;
			}
			this._changeTabindices(focusedItems, $target);

			if (!bUpdateOnly) {
				$target[0].focus();
				this._triggerFocusEvent($target);
			}
		},

		_changeTabindices: function($oldFocusedItems, $newFocusItem) {
			// reset focusedItems
			$oldFocusedItems.attr({
				'aria-activedescendant': 'false',
				'tabindex': -1
			});

			$newFocusItem.attr({
				'aria-activedescendant': 'true',
				'tabindex': 0
			});
		},

		onDeleteItem: function() {},

		onToggleItem: function(event) {
			var $target = $(this.getTarget(event.target, this.itemClass));
			event.preventDefault();
			this.toggleItem($target);
		},

		setFocusOnItem: function($item, bUpdateOnly) {
			this.onItemFocus({
				target: $item[0]
			}, bUpdateOnly);
		},

		prepareListItem: function(item) {
			return item;
		},

		prepareTreeItem: function(item) {
			return this.prepareListItem(item);
		},

		getListItems: function() {
			return Promise.resolve([]);
		},

		getRootItems: function() {
			return this.getListItems();
		},

		getChildItems: function( /* id, $parentItem */ ) {
			return Promise.resolve([]);
		},

		_getRequestFunction: function() {
			return (this.isTree) ? this.getRootItems.bind(this) : this.getListItems.bind(this);
		},

		_getPrepareFunction: function() {
			return (this.isTree) ? this.prepareTreeItem : this.prepareListItem;
		},

		render: function() {
			if (this.elementClass) {
				this.$el.addClass(this.elementClass);
			}
			var spinnerContainer = document.createElement('div');
			this.$el.append(spinnerContainer);
			ReactDOM.render(CenterLoadingSpinner({ variant: 'circle', size: 'normal' }), spinnerContainer);
			return Promise.resolve().then(()=>{
				var reqFn = this._getRequestFunction();
				return reqFn();
			}).then((listItems) => {
				ReactDOM.unmountComponentAtNode(spinnerContainer);
				$(spinnerContainer).remove();
				if (!this.dotTemplate) {
					// this list has been removed.
					throw new BaseError('the list has been removed');
				}
				var prepareFn = this._getPrepareFunction();
				var templateValues = _.extend({
					'controlType': this.controlClass,
					'level': 1,
					'items': listItems.map(prepareFn.bind(this)),
					'lbl_add': stringResources.get('sidebarAdd'),
					'lbl_clear': stringResources.get('clearTextFilterValue'),
					'lbl_missingDataSetData': stringResources.get('missingDataSetData')
				}, this.getCustomRenderProperties());
				var sHtml = this.dotTemplate(templateValues);

				this.$el.empty().append(sHtml);
				this.setElement(this.$el);

				if (this.canMultiSelect) {
					this._updateMultiSelectUI();
				}

				this._setInitialFocus();

				// render complete callback
				this.renderComplete();
			});
		},

		/**
			 * extenders should implement this function for specific render
			 * properties they have for dot
			 * @abstract
			 */
		getCustomRenderProperties: function() {
			return {};
		},

		/**
			 * callback function for any post-processing
			 * @callback
			 */
		renderComplete: function() {
			/* to be overrriden */
		},

		renderChildren: function(items, $parentItem) {
			var prepareFn = this._getPrepareFunction();
			var level = $parentItem.attr('aria-level');
			level = level ? Number(level) + 1 : 1;
			var sHtml = this.dotListTemplate({
				controlType: this.controlClass,
				items: items.map(prepareFn.bind(this)),
				listOnly: true,
				level: level
			});
			$parentItem.next().empty().attr('data-loaded', 'true').append(sHtml);
		},

		renderTree: function(items) {
			this.renderList(items);
		},

		renderList: function(items) {
			var sHtml = this._getListItemsHtml(items);
			this.$(this.controlClassSelector).empty();
			this.$(this.controlClassSelector).append(sHtml);
		},

		_setInitialFocus: function() {
			var firstItem = this.$(this.itemClassSelector).first();
			if (firstItem.length) {
				this.setFocusOnItem(firstItem);
			}
		},

		/**
			 * Generates html for insertion into the dom from the list of items that are passed in.
			 * This returns the list of items without the list element/div wrapper.
			 * @protected
			 */
		_getListItemsHtml: function(items) {
			var prepareFn = this._getPrepareFunction();
			var templateValues = _.extend({
				controlType: this.controlClass,
				items: items.map(prepareFn.bind(this)),
				listOnly: true,
				level: 1
			}, this.getCustomRenderProperties());
			return this.dotListTemplate(templateValues);
		},

		expandItem: function($parentItem) {
			if ($parentItem.attr('aria-expanded')) {
				var $childContainer = $parentItem.next();
				if ($childContainer.attr('data-loaded') === 'true') {
					$parentItem.attr('aria-expanded', 'true');
					$parentItem.children().first().removeClass('wfg_twisty-closed').addClass('wfg_twisty-open');
					$childContainer.show();
				} else {
					return Promise.resolve(this.getChildItems($parentItem.attr('data-id'), $parentItem))
						.then((childItems) => {
							if (!childItems || childItems.length === 0) {
								$parentItem.removeAttr('aria-expanded');
								$parentItem.children().first().removeClass('wfg_twisty-open wfg_twisty-closed');
								$childContainer.attr('data-loaded', 'true');
							} else {
								this.renderChildren(childItems, $parentItem);
								$parentItem.attr('aria-expanded', 'true');
								$parentItem.children().first().removeClass('wfg_twisty-closed').addClass('wfg_twisty-open');
								$childContainer.show();
							}
						});
				}
			}

			return Promise.resolve();
		},

		collapseItem: function($parentItem) {
			if ($parentItem.attr('aria-expanded')) {
				$parentItem.attr('aria-expanded', 'false').next().hide();
				$parentItem.children().first().removeClass('wfg_twisty-open').addClass('wfg_twisty-closed');
			}
		},

		toggleItem: function($parentItem) {
			var sExpanded = $parentItem.attr('aria-expanded');
			if (sExpanded === 'true') {
				this.collapseItem($parentItem);
			} else if (sExpanded === 'false') {
				this.expandItem($parentItem);
			}
		},

		setItemSelected: function($target) {
			$target.attr('aria-selected', true);
		},

		showError: function(msg, details) {
			var sHtml = '<div class="dashboardMessageDialog error">' + stringResources.get(msg);
			if (details) {
				sHtml += '<br><code>' + details + '</code>';
			}
			this.$el.empty().append(sHtml + '</div>');
		},

		remove: function() {
			this.$(this.itemClassSelector).remove();
			this.dotTemplate = null;

			View.inherited('remove', this, arguments);
			return this;
		}

	});
	return View;
});
